import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, ShieldCheck, KeyRound, Banknote, Wallet,
  CheckCircle2, Loader2, Phone, Building2, User as UserIcon,
  Activity, Truck,
} from "lucide-react";
import BankDemoShell from "./BankDemoShell";
import AuditPanel from "./AuditPanel";
import { newTrail, addEvent } from "./auditTrail";

// /bank-demo/seller — sandboxed 5-step seller signup walkthrough.
// Mirrors the buyer flow but emphasises payout/wallet provisioning. No real
// API calls.

const PREFILL = {
  first_name: "Maya",
  last_name: "Patel",
  email: "maya@glowco-demo.com",
  phone: "+1 (415) 555-0114",
  legal_name: "GlowCo Beauty Inc.",
  ein: "82-9911742",
  business_type: "C-Corp",
  trade_name: "GlowCo Beauty",
  address: "5012 Folsom St",
  city: "San Francisco",
  state: "CA",
  postal_code: "94110",
  owner_name: "Maya Patel",
  owner_dob: "1986-09-22",
  owner_ssn_last4: "0098",
  categories: ["Beauty & Personal Care", "Health & Wellness"],
  gmv_band: "$1M – $10M",
  fulfillment_country: "United States",
  year_founded: "2018",
};

const STEPS = [
  { id: 1, label: "Account",          icon: UserIcon },
  { id: 2, label: "Business · KYB",   icon: Building2 },
  { id: 3, label: "Owner · KYC",      icon: KeyRound },
  { id: 4, label: "Payout bank",      icon: Banknote },
  { id: 5, label: "Wallet & live",    icon: Wallet },
];

export default function BankDemoSellerFlow() {
  const [step, setStep] = useState(1);
  const [trail, setTrail] = useState(newTrail());
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState({});

  const pct = useMemo(() => Math.round(((step - 1) / (STEPS.length - 1)) * 100), [step]);

  const fireControl = async (events, finalize) => {
    setRunning(true);
    for (const ev of events) {
      const pending = { ...ev, status: "pending" };
      setTrail((t) => addEvent(t, pending));
      // eslint-disable-next-line no-await-in-loop
      await sleep(700 + Math.random() * 350);
      setTrail((t) => replacePending(t, ev.action, { ...ev, status: ev.finalStatus || "success" }));
    }
    setDone((d) => ({ ...d, [step]: true }));
    if (finalize) finalize();
    setRunning(false);
  };

  const goto = (n) => {
    if (n >= 1 && n <= STEPS.length) setStep(n);
  };

  return (
    <BankDemoShell
      crumbs={[
        { label: "Bank Demo", to: "/bank-demo" },
        { label: "Seller flow" },
      ]}
    >
      {/* Stepper ───────────────────────────────────────────────── */}
      <div className="mb-8" data-testid="bank-demo-seller-stepper">
        <div className="flex flex-wrap items-center gap-3">
          {STEPS.map((s) => {
            const active = s.id === step;
            const past = s.id < step;
            return (
              <button
                key={s.id}
                onClick={() => goto(s.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[12.5px] font-medium border transition-colors ${
                  active
                    ? "bg-white text-[#0b0d12] border-white"
                    : past
                    ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/25"
                    : "bg-white/5 text-white/55 border-white/15 hover:text-white"
                }`}
                data-testid={`bank-demo-seller-step-${s.id}`}
              >
                <s.icon className="w-3.5 h-3.5" />
                {s.id}. {s.label}
              </button>
            );
          })}
        </div>
        <div className="mt-4 h-1 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full bg-emerald-400 transition-all duration-500"
            style={{ width: `${pct}%` }}
            data-testid="bank-demo-seller-progress"
          />
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 md:p-8">
          {step === 1 && (
            <Step1Account
              running={running}
              done={!!done[1]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Seller · Maya Patel",
                      action: "auth.signup.attempted",
                      resource: "user:demo_seller",
                      meta: "Email + phone captured · password ≥ 8 chars · bcrypt(12) hashed",
                      finalStatus: "success",
                    },
                    {
                      actor: "Twilio Verify",
                      action: "sms.otp.verified",
                      resource: `phone:${PREFILL.phone}`,
                      meta: "OTP sent to +1 (415) ●●●-0114 · trust cookie issued (30d)",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(2),
                )
              }
            />
          )}
          {step === 2 && (
            <Step2KYB
              running={running}
              done={!!done[2]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Middesk",
                      action: "kyb.business.verified",
                      resource: `ein:${PREFILL.ein}`,
                      meta: `EIN matches "GlowCo Beauty Inc." · Delaware Sec. of State: ACTIVE · 6 yrs in good standing`,
                      finalStatus: "success",
                    },
                    {
                      actor: "Compliance · OFAC scan",
                      action: "ofac.business.scanned",
                      resource: `ein:${PREFILL.ein}`,
                      meta: "0 hits SDN · 0 hits Sectoral · cleared 2026-02-08",
                      finalStatus: "success",
                    },
                    {
                      actor: "Compliance · MSB classification",
                      action: "compliance.msb.classified",
                      resource: `ein:${PREFILL.ein}`,
                      meta: "NAICS 32562 (Toilet Preparation Mfg) — non-MSB · cleared",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(3),
                )
              }
            />
          )}
          {step === 3 && (
            <Step3KYC
              running={running}
              done={!!done[3]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Compliance · KYC",
                      action: "kyc.owner.verified",
                      resource: "owner:maya_patel",
                      meta: `DOB 1986-09-22 (age 39 · 18+ ✓) · last-4 SSN 0098 · ID match`,
                      finalStatus: "success",
                    },
                    {
                      actor: "Compliance · OFAC scan",
                      action: "ofac.individual.scanned",
                      resource: "owner:maya_patel",
                      meta: "0 hits SDN · 0 PEP · 0 adverse media",
                      finalStatus: "success",
                    },
                    {
                      actor: "Compliance · 25%+ owner rule",
                      action: "kyc.beneficial_owners.captured",
                      resource: "tenant:glowco_demo",
                      meta: "1 of 1 beneficial owners captured (≥25% ownership rule satisfied)",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(4),
                )
              }
            />
          )}
          {step === 4 && (
            <Step4Payout
              running={running}
              done={!!done[4]}
              onContinue={() =>
                fireControl(
                  [
                    {
                      actor: "Plaid · Link",
                      action: "plaid.item.linked",
                      resource: "plaid_item:item_8e21",
                      meta: "Mercury · 2 accounts · 90d history · ACH-eligible",
                      finalStatus: "success",
                    },
                    {
                      actor: "Plaid · Auth · ownership",
                      action: "plaid.account.ownership_verified",
                      resource: "plaid_item:item_8e21",
                      meta: "Account holder name matches business legal name · ownership ✓",
                      finalStatus: "success",
                    },
                    {
                      actor: "ACH · micro-deposit funding-source verification",
                      action: "ach.funding_source.verified",
                      resource: "funding_source:fs_8e21_op",
                      meta: "Operating account whitelisted as payout destination",
                      finalStatus: "success",
                    },
                  ],
                  () => setStep(5),
                )
              }
            />
          )}
          {step === 5 && (
            <Step5Live
              running={running}
              done={!!done[5]}
              onLive={() =>
                fireControl(
                  [
                    {
                      actor: "TabPay · Wallet provisioner",
                      action: "wallet.provisioned",
                      resource: "wallet:wal_glow_3a",
                      meta: "Sub-ledger created · TabPay account opened · routing whitelisted",
                      finalStatus: "success",
                    },
                    {
                      actor: "Risk engine · TX monitoring",
                      action: "monitoring.rules.activated",
                      resource: "seller:glow_3a",
                      meta: "Velocity, refund-abuse, geography, payout-mismatch rules: ON",
                      finalStatus: "success",
                    },
                  ],
                  () => {/* stays on this step; user may restart or jump */},
                )
              }
            />
          )}

          {/* Footer nav (Back) ───────────────────────────────── */}
          <div className="mt-8 pt-6 border-t border-white/10 flex items-center justify-between">
            <button
              type="button"
              disabled={step === 1 || running}
              onClick={() => goto(step - 1)}
              className="inline-flex items-center gap-1.5 text-[13px] text-white/65 hover:text-white disabled:text-white/30 disabled:cursor-not-allowed"
              data-testid="bank-demo-seller-back"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>
            <Link
              to="/bank-demo"
              className="text-[12.5px] text-white/55 hover:text-white"
              data-testid="bank-demo-seller-exit"
            >
              Exit demo
            </Link>
          </div>
        </section>

        <AuditPanel trail={trail} />
      </div>
    </BankDemoShell>
  );
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const replacePending = (trail, action, replacement) => {
  const next = [...trail];
  for (let i = next.length - 1; i >= 0; i -= 1) {
    if (next[i].action === action && next[i].status === "pending") {
      next[i] = { ...next[i], ...replacement };
      break;
    }
  }
  return next;
};

function StepHeader({ step, total, title, sub, control }) {
  return (
    <div className="border-b border-white/10 pb-5 mb-6">
      <div className="text-[11.5px] uppercase tracking-wide text-white/55">
        Step {step} of {total} · {control}
      </div>
      <h2 className="mt-2 text-2xl md:text-3xl font-semibold tracking-tight">{title}</h2>
      <p className="mt-2 text-[14px] text-white/60 leading-relaxed">{sub}</p>
    </div>
  );
}

function PrefillField({ label, value, hint }) {
  return (
    <div>
      <div className="text-[11.5px] uppercase tracking-wide text-white/50 mb-1">{label}</div>
      <div className="rounded-lg border border-white/10 bg-white/5 px-3.5 py-2.5 text-[13.5px] font-medium text-white/90">
        {value}
      </div>
      {hint && <div className="text-[11.5px] text-white/45 mt-1.5">{hint}</div>}
    </div>
  );
}

function ContinueBtn({ onClick, running, done, label, doneLabel = "Continue", testid }) {
  return (
    <button
      onClick={onClick}
      disabled={running}
      className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b0d12] px-5 py-3 text-[14px] font-semibold hover:bg-white/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      data-testid={testid}
    >
      {running ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" /> Running compliance check…
        </>
      ) : done ? (
        <>{doneLabel} <ArrowRight className="w-4 h-4" /></>
      ) : (
        <>{label} <ArrowRight className="w-4 h-4" /></>
      )}
    </button>
  );
}

function Step1Account({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={1}
        total={5}
        control="Twilio SMS step-up"
        title="Create the seller account"
        sub="Identical authentication front-door as the buyer flow — Twilio Verify OTP, bcrypt(12) password hashing, 30-day device-trust cookie."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="First name" value={PREFILL.first_name} />
        <PrefillField label="Last name"  value={PREFILL.last_name} />
        <PrefillField label="Email"      value={PREFILL.email} hint="MX-validated · disposable-domain blocklist applied" />
        <PrefillField label="Phone"      value={PREFILL.phone} hint="Twilio carrier-type lookup · VOIP rejected" />
      </div>
      <div className="mt-6 rounded-xl border border-sky-500/20 bg-sky-500/10 p-4 text-[13px] text-sky-100">
        <div className="flex items-center gap-2 font-medium"><Phone className="w-4 h-4" /> What happens next</div>
        <p className="mt-1.5 text-sky-100/85 leading-relaxed">
          Clicking <span className="font-mono">Continue</span> simulates the Twilio Verify SMS round-trip and audits a
          successful OTP match.
        </p>
      </div>
      <div className="mt-6">
        <ContinueBtn onClick={onContinue} running={running} done={done} label="Send OTP & continue" testid="bank-demo-seller-step1-continue" />
      </div>
    </>
  );
}

function Step2KYB({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={2}
        total={5}
        control="Middesk KYB · OFAC SDN · MSB classification"
        title="Verify the seller's business"
        sub="In addition to the standard KYB stack, sellers are run through an MSB classifier (per FinCEN MSB definition) to confirm the entity is not a money-services business — verqo is not equipped to onboard MSBs."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="Legal name"   value={PREFILL.legal_name} />
        <PrefillField label="Trade name"   value={PREFILL.trade_name} />
        <PrefillField label="EIN"          value={PREFILL.ein} hint="Format-validated 9-digit federal tax ID" />
        <PrefillField label="Business type" value={PREFILL.business_type} />
        <PrefillField label="Year founded"  value={PREFILL.year_founded} />
        <PrefillField label="Fulfillment country" value={PREFILL.fulfillment_country} />
        <div className="sm:col-span-2">
          <PrefillField label="Registered address" value={`${PREFILL.address}, ${PREFILL.city}, ${PREFILL.state} ${PREFILL.postal_code}`} />
        </div>
      </div>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-[13px] text-emerald-100">
        <div className="flex items-center gap-2 font-medium"><ShieldCheck className="w-4 h-4" /> KYB controls fired on continue</div>
        <ul className="mt-2 space-y-1 list-disc list-inside text-emerald-100/85">
          <li>Middesk: business name + EIN match · SoS active · 6 yrs in good standing</li>
          <li>OFAC: SDN, Sectoral, Non-SDN · 0 expected hits</li>
          <li>NAICS 32562 (cosmetics manufacturing) classified non-MSB</li>
        </ul>
      </div>
      <div className="mt-6">
        <ContinueBtn onClick={onContinue} running={running} done={done} label="Run KYB & continue" testid="bank-demo-seller-step2-continue" />
      </div>
    </>
  );
}

function Step3KYC({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={3}
        total={5}
        control="KYC · 18+ DOB · OFAC + PEP · 25%+ owner rule"
        title="Verify the beneficial owner(s)"
        sub="Per FinCEN's CDD final rule, every owner ≥ 25% must be captured. We also require one control person regardless of ownership %. Both are screened against OFAC + PEP + adverse media."
      />
      <div className="grid sm:grid-cols-2 gap-4">
        <PrefillField label="Owner legal name" value={PREFILL.owner_name} />
        <PrefillField label="Date of birth"    value={PREFILL.owner_dob} hint="Validated 18+ · max 120y" />
        <PrefillField label="Last 4 of SSN"    value={`●●●-●●-${PREFILL.owner_ssn_last4}`} hint="Encrypted at rest · only last 4 retained" />
        <PrefillField label="Owner phone"      value={PREFILL.phone} />
      </div>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-[13px] text-emerald-100">
        <div className="flex items-center gap-2 font-medium"><KeyRound className="w-4 h-4" /> KYC controls fired on continue</div>
        <ul className="mt-2 space-y-1 list-disc list-inside text-emerald-100/85">
          <li>OFAC + PEP + adverse media · 0 expected hits</li>
          <li>18+ check satisfied (current age 39)</li>
          <li>1 of 1 beneficial owners captured (≥ 25% rule)</li>
        </ul>
      </div>
      <div className="mt-6">
        <ContinueBtn onClick={onContinue} running={running} done={done} label="Run KYC & continue" testid="bank-demo-seller-step3-continue" />
      </div>
    </>
  );
}

function Step4Payout({ running, done, onContinue }) {
  return (
    <>
      <StepHeader
        step={4}
        total={5}
        control="Plaid Auth · ACH funding-source verification"
        title="Link the payout bank account"
        sub="The seller links their operating account via Plaid. We verify account ownership matches the business legal name (not just the signup user) and whitelist this destination as the only valid payout target — preventing payout-redirect fraud."
      />
      <div className="rounded-xl border border-white/10 bg-white/5 p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1a1f3b] grid place-items-center text-white font-bold">M</div>
          <div>
            <div className="text-[14px] font-semibold">Plaid Link · sandbox</div>
            <div className="text-[12px] text-white/55">Institution: Mercury · 2 accounts</div>
          </div>
        </div>
        <ul className="mt-4 divide-y divide-white/5">
          {[
            { name: "Operating · payout target", acct: "•••• 0042", bal: "$427,109.40", flag: "Whitelisted" },
            { name: "Tax escrow",                acct: "•••• 8821", bal: "$112,500.00" },
          ].map((a) => (
            <li key={a.acct} className="py-2.5 flex items-center justify-between text-[13px]">
              <div>
                <div className="font-medium flex items-center gap-2">
                  {a.name}
                  {a.flag && <span className="text-[10.5px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">{a.flag}</span>}
                </div>
                <div className="text-white/55 text-[12px]">{a.acct}</div>
              </div>
              <div className="font-mono">{a.bal}</div>
            </li>
          ))}
        </ul>
      </div>
      <div className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/10 p-4 text-[13px] text-emerald-100">
        <div className="flex items-center gap-2 font-medium"><Banknote className="w-4 h-4" /> Payout-bank controls</div>
        <ul className="mt-2 space-y-1 list-disc list-inside text-emerald-100/85">
          <li>Plaid Auth: account holder = business legal name (not the user)</li>
          <li>ACH funding-source verified (NACHA Web/PPD eligible)</li>
          <li>Bank changes require fresh Twilio step-up + 24h hold</li>
        </ul>
      </div>
      <div className="mt-6">
        <ContinueBtn onClick={onContinue} running={running} done={done} label="Confirm payout bank & continue" testid="bank-demo-seller-step4-continue" />
      </div>
    </>
  );
}

function Step5Live({ running, done, onLive }) {
  return (
    <>
      <StepHeader
        step={5}
        total={5}
        control="TabPay wallet provisioning · TX monitoring"
        title="Provision wallet & go live"
        sub="A sub-ledger wallet is opened on TabPay. The wallet whitelists the seller's verified payout bank as the only valid destination, and TX monitoring rules arm before the seller can accept their first order."
      />
      <div className="grid sm:grid-cols-3 gap-4">
        <Tile k="Payouts" v="D+1 ACH" sub="RTP available post-tier-up" tone="emerald" />
        <Tile k="Wallet" v="TabPay" sub="Sub-ledger · multi-currency ready" tone="white" />
        <Tile k="Settlement currency" v="USD" sub="GBP/EUR enabled on request" tone="white" />
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-[12px] uppercase tracking-wide text-white/55 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Active controls</div>
          <ul className="mt-3 space-y-1.5 text-[13px] text-white/85">
            <li>· Step-up SMS on bank-account changes</li>
            <li>· Payout-mismatch flag if order ship-to ≠ buyer registered address</li>
            <li>· Refund-abuse rule: ≥ 5 refunds/24h · same buyer</li>
            <li>· Velocity rule: ≥ 50% MoM new-buyer growth flagged for review</li>
            <li>· Re-screen OFAC every 24h (incremental)</li>
          </ul>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
          <div className="text-[12px] uppercase tracking-wide text-white/55 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Sponsor bank deliverables</div>
          <ul className="mt-3 space-y-1.5 text-[13px] text-white/85">
            <li>· Daily payout JSONL → bank S3 (signed Ed25519)</li>
            <li>· Tier-up history (immutable)</li>
            <li>· Real-time webhook on AML hits ≥ HIGH</li>
            <li>· Quarterly SAR-eligible bundle</li>
            <li>· Payout-bank change events surfaced inline</li>
          </ul>
        </div>
      </div>
      <div className="mt-7 flex flex-wrap gap-3">
        <button
          onClick={onLive}
          disabled={done || running}
          className="inline-flex items-center gap-2 rounded-full bg-white text-[#0b0d12] px-5 py-2.5 text-[13.5px] font-semibold hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="bank-demo-seller-step5-go-live"
        >
          {running ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Provisioning wallet…</>
          ) : done ? (
            <><CheckCircle2 className="w-4 h-4" /> Wallet live · seller ready</>
          ) : (
            <><Activity className="w-4 h-4" /> Provision wallet & go live</>
          )}
        </button>
        <Link
          to="/bank-demo/buyer"
          className="inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[13.5px] font-medium hover:bg-white/5"
          data-testid="bank-demo-seller-go-buyer"
        >
          See the buyer flow <ArrowRight className="w-4 h-4" />
        </Link>
        <Link
          to="/bank-demo"
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[13.5px] font-medium text-white/65 hover:text-white"
          data-testid="bank-demo-seller-back-to-landing"
        >
          Back to overview
        </Link>
      </div>
    </>
  );
}

function Tile({ k, v, sub, tone }) {
  const cls = tone === "emerald" ? "border-emerald-500/25 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]";
  return (
    <div className={`rounded-xl border ${cls} p-5`}>
      <div className="text-[11.5px] uppercase tracking-wide text-white/55">{k}</div>
      <div className={`mt-2 text-2xl font-semibold tracking-tight ${tone === "emerald" ? "text-emerald-300" : ""}`}>{v}</div>
      <div className="text-[12px] text-white/55 mt-0.5">{sub}</div>
    </div>
  );
}
